import { Type, type Static } from '@sinclair/typebox';
import { SeoBriefSchema, SeoContentType, SeoSearchIntent, SeoTargetKind } from './seo-brief.schema';
import { SeoTargetProfileSchema } from './seo-target-profile.schema';

/**
 * Contrats des routes `/internal/seo/*` (plan §4.0) : l'admin Payload
 * (Propelsia) et l'orchestration client appellent le moteur avec ces objets.
 * Les traitements longs (découverte, brief, rédaction) sont des jobs BullMQ :
 * la route renvoie un `job_id`, le client interroge `GET /internal/seo/jobs/:id`.
 */

/** Cible + surcharge éventuelle du profil (Propelsia : l'index des pages vient de Payload). */
const TargetFields = {
  target_kind: Type.Enum(SeoTargetKind),
  professional_id: Type.Optional(Type.Integer({ minimum: 1 })),
  /** Champs de profil fournis par l'appelant, fusionnés sur le profil de base de la cible. */
  profile: Type.Optional(Type.Partial(SeoTargetProfileSchema)),
};

export const SeoDiscoverRequestSchema = Type.Object({
  ...TargetFields,
  /** Nombre de sujets voulus (défaut 12). */
  max: Type.Optional(Type.Integer({ minimum: 1, maximum: 60 })),
  /** Mois éditorial visé (`YYYY-MM`), clients uniquement. */
  target_month: Type.Optional(Type.String({ pattern: '^\\d{4}-\\d{2}$' })),
  /** Sujets libres à faire passer par le même pipeline. */
  manual: Type.Optional(Type.Array(Type.String({ minLength: 2, maxLength: 120 }), { maxItems: 20 })),
  /**
   * Ne traiter QUE les sujets libres (pas d'expansion par autocomplete, SERP, volumes ni
   * concurrents) : « Ajouter ce sujet » depuis l'app, qui ne doit ni coûter une découverte
   * complète ni empiler d'autres propositions.
   */
  manual_only: Type.Optional(Type.Boolean()),
  /**
   * Recherche demandée depuis l'app (bouton « proposer » / « demander d'autres sujets ») : le pro
   * est devant l'écran, pas d'e-mail « choisissez vos sujets » tout de suite. Un rappel part après
   * `SEO_TOPICS_IN_APP_NOTIFY_DELAY_MS` s'il reste des sujets à trancher (il a quitté la page).
   */
  in_app: Type.Optional(Type.Boolean()),
});
export type SeoDiscoverRequestObject = Static<typeof SeoDiscoverRequestSchema>;

export const SeoBriefRequestSchema = Type.Object({
  ...TargetFields,
  keyword: Type.String({ minLength: 2, maxLength: 160 }),
  intent: Type.Optional(Type.Enum(SeoSearchIntent)),
  content_type: Type.Optional(Type.Enum(SeoContentType)),
  area: Type.Optional(Type.String({ maxLength: 120 })),
  notes: Type.Optional(Type.Array(Type.String({ maxLength: 400 }), { maxItems: 10 })),
  /** Mot-clé déjà enregistré côté moteur (sinon créé à la volée). */
  seo_keyword_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type SeoBriefRequestObject = Static<typeof SeoBriefRequestSchema>;

export const SeoWriteRequestSchema = Type.Object({
  ...TargetFields,
  /** Brief complet, éventuellement retouché par l'humain. */
  brief: SeoBriefSchema,
  /** Brief déjà enregistré côté moteur (sinon créé à la volée). */
  seo_brief_id: Type.Optional(Type.Integer({ minimum: 1 })),
  max_revisions: Type.Optional(Type.Integer({ minimum: 0, maximum: 3 })),
  judge: Type.Optional(Type.Boolean()),
  /** Chercher et attacher des images (Pexels) après la rédaction. */
  images: Type.Optional(Type.Boolean()),
});
export type SeoWriteRequestObject = Static<typeof SeoWriteRequestSchema>;

export const SeoScoreRequestSchema = Type.Object({
  html: Type.String({ minLength: 1 }),
  /** Questions / réponses éditées à part du corps (notées comme une section FAQ en fin d'article). */
  faq: Type.Optional(Type.Array(Type.Object({ question: Type.String(), answer: Type.String() }))),
  title: Type.Optional(Type.String()),
  meta_title: Type.Optional(Type.String()),
  meta_description: Type.Optional(Type.String()),
  excerpt: Type.Optional(Type.String()),
  slug: Type.Optional(Type.String()),
  focus_keyword: Type.Optional(Type.String()),
  secondary_keywords: Type.Optional(Type.Array(Type.String())),
  brief: Type.Optional(SeoBriefSchema),
  target_kind: Type.Optional(Type.Enum(SeoTargetKind)),
  internal_hosts: Type.Optional(Type.Array(Type.String())),
  strict: Type.Optional(Type.Boolean()),
});
export type SeoScoreRequestObject = Static<typeof SeoScoreRequestSchema>;

export const SeoBacklinksRequestSchema = Type.Object({
  ...TargetFields,
  /** Article moteur déjà publié (`published_url` renseigné via l'orchestration). */
  seo_article_id: Type.Integer({ minimum: 1 }),
});
export type SeoBacklinksRequestObject = Static<typeof SeoBacklinksRequestSchema>;

/** L'orchestration signale qu'un article moteur est en ligne (URL publique, référence dans la cible). */
export const SeoPublishedRequestSchema = Type.Object({
  seo_article_id: Type.Integer({ minimum: 1 }),
  published_url: Type.String({ minLength: 8, maxLength: 500 }),
  published_ref: Type.Optional(Type.String({ maxLength: 120 })),
});
export type SeoPublishedRequestObject = Static<typeof SeoPublishedRequestSchema>;

/**
 * Rattrapage en lot (tâche horaire Payload) : toutes les publications en ligne portant un
 * article moteur. Le backend ne marque que celles encore READY (idempotent).
 */
export const SeoPublishedSyncRequestSchema = Type.Object({
  items: Type.Array(SeoPublishedRequestSchema, { maxItems: 500 }),
});
export type SeoPublishedSyncRequestObject = Static<typeof SeoPublishedSyncRequestSchema>;

/**
 * Backlink offert à un client (plan MAX : un par mois) publié sur le blog Propelsia : Payload
 * le signale à la publication de l'article porteur. Idempotent sur (`professional_id`, `source_ref`).
 */
export const SeoClientBacklinkRequestSchema = Type.Object({
  professional_id: Type.Integer({ minimum: 1 }),
  source_url: Type.String({ minLength: 8, maxLength: 500 }),
  source_title: Type.Optional(Type.String({ maxLength: 255 })),
  source_ref: Type.Optional(Type.String({ maxLength: 120 })),
  anchor: Type.Optional(Type.String({ maxLength: 255 })),
  target_url: Type.Optional(Type.String({ maxLength: 500 })),
  /** Date de publication de l'article porteur (défaut : maintenant). */
  published_at: Type.Optional(Type.String({ format: 'date-time' })),
});
export type SeoClientBacklinkRequestObject = Static<typeof SeoClientBacklinkRequestSchema>;

export enum SeoJobKind {
  DISCOVER = 'discover',
  BRIEF = 'brief',
  WRITE = 'write',
  /** Clients : brief → rédaction → brouillon `pro_news` en un seul job (plan §5.5). */
  PRODUCE = 'produce',
  /** Clients : e-mail « choisissez vos sujets » différé après une recherche demandée depuis l'app. */
  NOTIFY_TOPICS = 'notify_topics',
  /** Admin : rejouer le contrôle qualité (score + juge) d'un article de pro déjà rédigé. */
  REQUALIFY = 'requalify',
}

/**
 * Délai avant l'e-mail « choisissez vos sujets » quand la recherche a été demandée depuis l'app :
 * le pro trie en général ses sujets dans la foulée ; passé ce délai, s'il en reste à trancher, il
 * a quitté la page et l'e-mail le ramène.
 */
export const SEO_TOPICS_IN_APP_NOTIFY_DELAY_MS = 30 * 60_000;

export const SeoNotifyTopicsRequestSchema = Type.Object({
  professional_id: Type.Integer({ minimum: 1 }),
  /** Mois éditorial dont les sujets attendent un tri (`YYYY-MM`). */
  target_month: Type.String({ pattern: '^\\d{4}-\\d{2}$' }),
});
export type SeoNotifyTopicsRequestObject = Static<typeof SeoNotifyTopicsRequestSchema>;

export const SeoProduceRequestSchema = Type.Object({
  professional_id: Type.Integer({ minimum: 1 }),
  seo_keyword_id: Type.Integer({ minimum: 1 }),
});
export type SeoProduceRequestObject = Static<typeof SeoProduceRequestSchema>;

export enum SeoJobStatus {
  WAITING = 'waiting',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  UNKNOWN = 'unknown',
}

export const SeoJobEnqueuedResponseSchema = Type.Object({
  job_id: Type.String(),
  kind: Type.Enum(SeoJobKind),
});
export type SeoJobEnqueuedResponseObject = Static<typeof SeoJobEnqueuedResponseSchema>;

export const SeoJobStatusResponseSchema = Type.Object({
  job_id: Type.String(),
  kind: Type.Enum(SeoJobKind),
  status: Type.Enum(SeoJobStatus),
  /** Étape en cours, texte court. */
  progress: Type.Optional(Type.String()),
  /** Résultat du job (forme selon `kind`, cf. moteur). */
  result: Type.Optional(Type.Any()),
  error: Type.Optional(Type.String()),
});
export type SeoJobStatusResponseObject = Static<typeof SeoJobStatusResponseSchema>;
